import { useEffect, useState } from 'react'
import { ButtonProps } from '../Button'
import { TableHorizontalScrollControlsProps } from './TableHorizontalScrollControls.types'

export const useButtonProps = (
  props: TableHorizontalScrollControlsProps
): {
  shouldRender: boolean
  maxScrollLeft: number
  scrollLeftButtonProps: Partial<ButtonProps>
  scrollRightButtonProps: Partial<ButtonProps>
} => {
  const {
    containerRef,
    onScrollLeft,
    onScrollWidthChange
  } = props

  const [shouldRender, setShouldRender] = useState<boolean>(false)
  const [maxScrollLeft, setMaxScrollLeft] = useState<number>(0)
  const [scrollLeft, setScrollLeft] = useState<number>(0)
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout>(null)

  const onScrollEvent = (ev: any) => {
    const newScrollLeft: number = ev?.target?.scrollLeft || 0
    setScrollLeft(newScrollLeft)
    onScrollLeft && onScrollLeft(newScrollLeft)
  }

  const updateShouldRenderState = () => {
    if (containerRef.current) {
      onScrollWidthChange && onScrollWidthChange(containerRef.current.scrollWidth)
      const maxScrollPixelsLeft = (containerRef.current.scrollWidth - containerRef.current.clientWidth) || 0
      const hasHorizontalScrollBar = maxScrollPixelsLeft > 0

      if (hasHorizontalScrollBar) {
        setMaxScrollLeft(maxScrollPixelsLeft)
        containerRef.current.addEventListener('scroll', onScrollEvent, false)
      } else {
        setMaxScrollLeft(0)
        setScrollLeft(0)
        onScrollLeft && onScrollEvent(0)
        containerRef.current.removeEventListener('scroll', onScrollEvent, false)
      }

      const shouldRenderStateShouldChange = hasHorizontalScrollBar !== shouldRender
      if (shouldRenderStateShouldChange) {
        setShouldRender(hasHorizontalScrollBar)
        clearIntervalId()
      }
    }
  }

  useEffect(() => {
    window.addEventListener('resize', updateShouldRenderState, false)
    return () => window.removeEventListener('resize', updateShouldRenderState, false)
    // eslint-disable-next-line
  }, [])

  setTimeout(updateShouldRenderState, 200)

  const scrollToLeft = () => {
    if (!containerRef.current) return
    containerRef.current.scrollBy({
      behavior: 'auto',
      left: -10
    })
  }

  const scrollToRight = () => {
    if (!containerRef.current) return
    containerRef.current.scrollBy({
      behavior: 'auto',
      left: 10
    })
  }

  const onMouseDownLeft = () => {
    clearIntervalId()
    scrollToLeft()
    setIntervalId(setInterval(scrollToLeft, 100))
  }

  const onMouseDownRight = () => {
    clearIntervalId()
    scrollToRight()
    setIntervalId(setInterval(scrollToRight, 100))
  }

  const clearIntervalId = () => {
    clearInterval(intervalId)
  }

  return {
    shouldRender,
    maxScrollLeft,
    scrollLeftButtonProps: {
      onMouseDown: onMouseDownLeft,
      onMouseUp: clearIntervalId,
      disabled: scrollLeft <= 0
    },
    scrollRightButtonProps: {
      onMouseDown: onMouseDownRight,
      onMouseUp: clearIntervalId,
      disabled: scrollLeft >= maxScrollLeft
    }
  }
}

export const useIsFocused = (shouldRender: boolean): {
  isFocused: boolean
  setOnFocus: () => void
  setOffFocus: () => void
} => {
  const [isFocused, setIsFocused] = useState<boolean>(true)
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>(null)

  useEffect(() => {
    if (!shouldRender) return
    setTimeoutId(setTimeout(() => setIsFocused(false), 2000))
  }, [shouldRender])

  const setOnFocus = () => {
    clearTimeout(timeoutId)
    setIsFocused(true)
  }
  const setOffFocus = () => {
    clearTimeout(timeoutId)
    setIsFocused(false)
  }

  return {
    isFocused,
    setOnFocus,
    setOffFocus
  }
}
